import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';

// By path, not through the shared barrel: the barrel reaches the reducer and
// back into this folder.
import { conversationUuidOf } from '../conversation/conversation-address.helper';
import { nextAutoSubmit } from '../conversation/prompt-input/removed-prompt-input.helpers';
import { selectAutoSendBatches } from '../shared/selectors/conversation-prompt.selectors';
import { ChatWithMessagesActions } from './chats.actions';

/**
 * SB-3944 / KD-58: the auto-send of a hidden-prompt skill, for every chat.
 * A ready batch is sent for its own chat, whether that chat is shown, another
 * chat is shown, or the person is outside the chat pages. It lived in the
 * composer, which saw the selected chat only.
 *
 * One latch per chat keeps SB-3538 T3 / KD-11's rule: a batch is sent once,
 * however often the store rebuilds. The latch is written only when a batch is
 * sendable, so a hold (a file still staged, a connector save) delays a batch
 * and never drops it.
 */
@Injectable()
export class ChatsAutoSendEffects {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly lastKeyByChatUuid = new Map<string, string | null>();

  autoSend$ = createEffect(
    () =>
      this.store.select(selectAutoSendBatches).pipe(
        tap((batches) => {
          // The chat on screen is the one the address names. Not the
          // selection: it stays when the person leaves the chat pages
          // (`ChatsRouterEffects`), and a transition started there would skip
          // the router's own.
          const shownChatUuid = conversationUuidOf(this.router.url);
          for (const batch of batches) {
            const next = nextAutoSubmit(
              this.lastKeyByChatUuid.get(batch.chatUuid) ?? null,
              batch.batchKey,
              batch.sendable,
            );
            this.lastKeyByChatUuid.set(batch.chatUuid, next.key);
            if (next.send) {
              this.send(
                ChatWithMessagesActions.addChatMessage({
                  chatUuid: batch.chatUuid,
                  content: '',
                  documents: batch.documents,
                }),
                batch.chatUuid === shownChatUuid,
              );
            }
          }
        }),
      ),
    { dispatch: false },
  );

  /**
   * The chat on screen keeps the send's view transition (KD-33, Check 6). A
   * chat that is not shown gets none: starting one would skip a transition
   * running in the chat on screen.
   */
  private send(
    action: ReturnType<typeof ChatWithMessagesActions.addChatMessage>,
    shown: boolean,
  ): void {
    if (shown && document.startViewTransition) {
      document.startViewTransition(() => this.store.dispatch(action));
      return;
    }
    this.store.dispatch(action);
  }
}
